import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Typography,
} from '@mui/material';
import {
  LinkOff as LinkOffIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';

import {
  getParcelMembersByRef,
  linkMemberByRef,
  unlinkMemberByRef,
} from '../../api/parcels';
import { listMembers } from '../../api/fpaMembers';
import { useMobileLayout } from '../../utils/useMobileLayout';

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Modal for managing the FPA-members linked to a parcel.
 *
 * Identifies the parcel by its cadastral LISKEY (`parcelRef`) since the
 * parcel data lives in S3 Parquet and has no DynamoDB-side int id. Calls
 * the by-ref endpoints; the parent receives `onChange()` after each
 * link/unlink so it can refresh its cached `linkedRefs` set and recolour
 * the polygon on the map.
 */
const MemberLinkDialog = ({
  open,
  onClose,
  parcelRef,
  parcelLabel,
  tenantId,
  onChange,
  initialSearch,
}) => {
  const { isMobile } = useMobileLayout();
  const [owners, setOwners] = useState([]);
  const [ownersLoading, setOwnersLoading] = useState(false);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  const refreshOwners = useCallback(async () => {
    if (!parcelRef) return;
    setOwnersLoading(true);
    setError(null);
    try {
      const data = await getParcelMembersByRef(parcelRef);
      setOwners(data?.owners || []);
    } catch (err) {
      console.error('Owner fetch failed:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load linked members');
      setOwners([]);
    } finally {
      setOwnersLoading(false);
    }
  }, [parcelRef]);

  useEffect(() => {
    if (open && parcelRef) {
      refreshOwners();
      // Prefill the member search with the parcel's plot number (ParcelNo) so
      // the matching member surfaces immediately; the typeahead effect runs it.
      setSearch(initialSearch || '');
      setResults([]);
    }
  }, [open, parcelRef, initialSearch, refreshOwners]);

  // Typeahead search for members to link.
  useEffect(() => {
    if (!open) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!search.trim()) {
      setResults([]);
      return undefined;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await listMembers({
          tenant_id: tenantId,
          search: search.trim(),
          page: 1,
          page_size: 25,
          // Non-members are property records too, and a parcel owned by one
          // is exactly what the map's grey "non-member" colour represents.
          // Without this the endpoint's members-only default makes that state
          // unreachable from the UI.
          member_type: 'all',
        });
        setResults(data?.fpa_members || data?.members || []);
      } catch (err) {
        console.error('Member search failed:', err);
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, tenantId, open]);

  const linkedMemberIds = useMemo(
    () => new Set(owners.map((o) => o.member_id)),
    [owners],
  );

  const handleLink = async (member) => {
    setError(null);
    try {
      await linkMemberByRef(parcelRef, member.id);
      await refreshOwners();
      if (onChange) onChange();
      setSearch('');
      setResults([]);
    } catch (err) {
      console.error('Link failed:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to link member');
    }
  };

  const handleUnlink = async (owner) => {
    if (!window.confirm(`Unlink ${owner.member_name || `member #${owner.member_id}`}?`)) return;
    setError(null);
    try {
      await unlinkMemberByRef(parcelRef, owner.member_id);
      await refreshOwners();
      if (onChange) onChange();
    } catch (err) {
      console.error('Unlink failed:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to unlink');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        Members linked to {parcelLabel || parcelRef || 'parcel'}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="subtitle2">Currently linked</Typography>
          {ownersLoading && <CircularProgress size={16} />}
        </Box>
        {owners.length === 0 && !ownersLoading && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No members linked to this parcel yet.
          </Typography>
        )}
        <List dense disablePadding>
          {owners.map((o) => (
            <ListItem key={o.member_id} divider>
              <ListItemText
                primary={o.member_name || `Member #${o.member_id}`}
                secondary={o.member_ref ? `Ref: ${o.member_ref}` : null}
              />
              <ListItemSecondaryAction>
                <IconButton edge="end" onClick={() => handleUnlink(o)} size="small">
                  <LinkOffIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <PersonAddIcon fontSize="small" />
          <Typography variant="subtitle2">Add a member</Typography>
        </Box>
        <TextField
          autoFocus
          size="small"
          fullWidth
          placeholder="Search by name, ref, address..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 1 }}
        />
        {searching && (
          <Box display="flex" justifyContent="center" py={1}>
            <CircularProgress size={20} />
          </Box>
        )}
        {!searching && search.trim() && results.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No members or non-members match — try a different term.
          </Typography>
        )}
        <List dense disablePadding>
          {results.map((m) => {
            const alreadyLinked = linkedMemberIds.has(m.id);
            return (
              <ListItem
                key={m.id}
                divider
                onClick={() => !alreadyLinked && handleLink(m)}
                sx={{
                  cursor: alreadyLinked ? 'default' : 'pointer',
                  opacity: alreadyLinked ? 0.5 : 1,
                  '&:hover': alreadyLinked ? {} : { backgroundColor: 'action.hover' },
                }}
              >
                <ListItemText
                  primary={m.name || `Member #${m.id}`}
                  secondary={[
                    m.ref_number && `Ref: ${m.ref_number}`,
                    m.plot_number && `Plot ${m.plot_number}`,
                    m.area_suburb,
                  ].filter(Boolean).join(' · ')}
                />
                {/* Every row is labelled, not just the unusual one — an
                    unlabelled row would read as "member" by default. */}
                <Chip
                  size="small"
                  variant="outlined"
                  sx={{ mr: alreadyLinked ? 1 : 0 }}
                  label={m.member_type === 'non_member' ? 'Non-member' : 'Member'}
                  color={m.member_type === 'non_member' ? 'default' : 'primary'}
                />
                {alreadyLinked && (
                  <Chip size="small" label="Linked" color="success" />
                )}
              </ListItem>
            );
          })}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MemberLinkDialog;
